/**
 * Perfil técnico de laboratorio: todos los umbrales del motor, en un solo sitio.
 *
 * @ported-from GuardIA1224/guardia-platform-sites@776c789 src/analysis/driving-analysis-v1.ts
 *
 * DIVERGENCIA-03 frente a V5: allá estos números aparecían dos veces — una en
 * el perfil (informativo) y otra como literales sueltos dentro de las funciones
 * de detección, que eran los que de verdad mandaban. Aquí el perfil es la única
 * fuente y las funciones lo leen. Los valores son idénticos y hay un test que
 * los compara uno por uno contra V5.
 *
 * Cambiar un umbral cambia la firma del análisis, y con razón: dos informes
 * calculados con perfiles distintos no son comparables.
 */

export const LAB_PROFILE = {
  profileId: "guardia-lab",
  version: 3,
  status: "DRAFT",
  timezone: "America/Bogota",

  /** Reglas que deciden si un punto o un tramo sirve para calcular. */
  quality: {
    minimumSatelliteCount: 4,
    maximumImplicitSpeedKmh: 300,
    totalDistanceTolerance: 0.01,
    maximumProviderGeometrySpeedDifferenceKmh: 50,
    maximumIsolatedDropoutGapSeconds: 5,
    minimumContinuousMotionSpeedKmh: 10,
    maximumBridgeSpeedDifferenceKmh: 25,
    /*
     * Posición repetida en plena marcha (ADR-010), en km/h: un punto a
     * velocidad 0 en la misma coordenada del anterior, cuando el anterior iba
     * a esta velocidad o más, es la fijación anterior repetida y no entra al
     * cálculo. En 0 queda apagada —el Pipeline V5 original, el que las pruebas
     * de paridad congelan—. Los perfiles de la familia la encienden
     * (`REPEATED_FIX_MINIMUM_SPEED_KMH`), y un perfil guardado antes de que
     * existiera la hereda encendida.
     */
    repeatedFixMinimumSpeedKmh: 0,
  },

  /** Corte de viajes: cuándo termina uno y empieza otro, y cuál cuenta. */
  trips: {
    maximumGapSeconds: 300,
    minimumTripDurationSeconds: 60,
    minimumTripDistanceMeters: 200,
    minimumEligibleSegments: 1,
  },

  /** Aceleración y frenada: cambios de velocidad a lo largo del avance. */
  longitudinal: {
    accelerationThresholdMps2: 2.5,
    brakingThresholdMps2: -3,
    minimumSpeedChangeKmh: 5,
    maximumMidpointGapSeconds: 60,
  },

  /** Giros: cambio de rumbo entre dos tramos consecutivos. */
  turns: {
    minimumHeadingChangeDegrees: 35,
    minimumYawRateDegreesPerSecond: 8,
    /*
     * Criterio de aceleración lateral del manual, en m/s². En 0 queda apagado
     * y manda la velocidad angular fija de arriba — que es el comportamiento
     * del Pipeline V5 original, el que las pruebas de paridad congelan. El
     * preset del manual lo pone en 3,4 y a partir de ahí manda este.
     */
    lateralAccelerationMps2: 0,
    minimumReferenceSpeedKmh: 15,
    minimumSegmentDistanceMeters: 10,
    maximumMidpointGapSeconds: 60,
  },

  /**
   * Velocidad elevada. 120 km/h es un umbral OPERACIONAL de laboratorio.
   * No es un límite legal de vía y la UI no lo puede presentar como infracción
   * (constitución, Art. 3).
   */
  elevatedSpeed: {
    operationalSpeedLimitKmh: 120,
    /*
     * Histéresis de salida, en km/h. En 0 queda apagada y la racha se corta en
     * cuanto la velocidad deja de superar `operationalSpeedLimitKmh` —que es
     * el Pipeline V5 original, el que las pruebas de paridad congelan—. Con un
     * valor mayor que 0 la racha abre al superar el límite y no cierra hasta
     * bajar de este número.
     *
     * La trampa que evita: sin histéresis, un vehículo que oscila alrededor
     * del límite abre una racha nueva CADA VEZ que lo cruza, y cada racha es
     * un episodio que alimenta la tasa por 100 km y con ella el DriveScore.
     * Medido en producción con el límite en 80 km/h, un equipo de 10 s produjo
     * 839 tramos por encima en 3.104 km, en tiradas de autopista donde la
     * velocidad ronda los 80–86 km/h: el mismo trecho contado cientos de
     * veces. El preset del manual lo pone en 76 y a partir de ahí manda este.
     *
     * Solo tiene efecto con `sustainedWindowSeconds` encendida, que es la que
     * hace que exista una racha; con la ventana apagada cada tramo se evalúa
     * suelto y no hay nada que mantener abierto.
     */
    operationalSpeedExitKmh: 0,
    minimumSegmentDurationSeconds: 5,
    /*
     * Ventana de velocidad sostenida, en segundos, sumando tramos
     * consecutivos. En 0 queda apagada y manda la regla vieja —un solo tramo
     * de `minimumSegmentDurationSeconds`—, que es el Pipeline V5 original.
     *
     * La regla vieja se vuelve imposible en cuanto el equipo reporta más
     * seguido que la duración pedida, así que se enciende desde el
     * laboratorio a conciencia y no en silencio.
     */
    sustainedWindowSeconds: 0,
    minimumSegmentDistanceMeters: 20,
    thresholdSource: "configured_operational_limit",
  },

  /** Bandas de severidad: [moderada, alta, crítica]. Por debajo, baja. */
  severity: {
    acceleration: [3.5, 4.5, 6],
    braking: [4, 5.5, 7],
    yawRate: [12, 20, 30],
    turnReferenceSpeed: [25, 40, 60],
    elevatedSpeedExcessPercent: [10, 25, 50],
    classification: "technical_laboratory_only",
  },
} as const;

/**
 * El valor con que la familia de perfiles enciende la regla de posición
 * repetida (ADR-010). Es el mismo umbral de «venía en marcha» del detector de
 * falso detenido: a 10 km/h detenerse recorre al menos medio metro, y la
 * coordenada se reporta con una resolución de unos 10 cm.
 */
export const REPEATED_FIX_MINIMUM_SPEED_KMH = 10;

export type LabProfile = typeof LAB_PROFILE;
export type SeverityBand = readonly [number, number, number];
